function floodFill(map, startX, startY) {
    const visited = Array(map.length).fill(null).map(() => Array(map[0].length).fill(false));
    const queue = [[startX, startY]];
    let count = 0;
    
    while (queue.length > 0) {
        const [x, y] = queue.shift();
        
        if (x < 0 || x >= map[0].length || y < 0 || y >= map.length) continue;
        if (visited[y][x] || map[y][x] !== 0) continue;
        
        visited[y][x] = true;
        count++;
        
        queue.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    
    return { visited, count };
}

function ensureConnectivity(map, rooms) {
    if (rooms.length === 0) return;
    
    // Keep connecting rooms until all are reachable
    let connectedRooms = new Set([0]);
    let unconnectedRooms = new Set();
    for (let i = 1; i < rooms.length; i++) {
        unconnectedRooms.add(i);
    }
    
    while (unconnectedRooms.size > 0) {
        let bestConnection = null;
        let minDistance = Infinity;
        
        // Find the closest pair of connected and unconnected rooms
        for (const connectedIdx of connectedRooms) {
            for (const unconnectedIdx of unconnectedRooms) {
                const room1 = rooms[connectedIdx];
                const room2 = rooms[unconnectedIdx];
                
                const x1 = Math.floor(room1.x + room1.width / 2);
                const y1 = Math.floor(room1.y + room1.height / 2);
                const x2 = Math.floor(room2.x + room2.width / 2);
                const y2 = Math.floor(room2.y + room2.height / 2);
                
                const distance = Math.abs(x2 - x1) + Math.abs(y2 - y1);
                
                if (distance < minDistance) {
                    minDistance = distance;
                    bestConnection = {
                        fromIdx: connectedIdx,
                        toIdx: unconnectedIdx,
                        x1, y1, x2, y2
                    };
                }
            }
        }
        
        if (bestConnection) {
            // Create L-shaped corridor
            let { x1, y1, x2, y2 } = bestConnection;
            
            // Randomly choose horizontal-first or vertical-first
            if (Math.random() < 0.5) {
                // Horizontal first
                while (x1 !== x2) {
                    map[y1][x1] = 0;
                    x1 += (x2 > x1) ? 1 : -1;
                }
                while (y1 !== y2) {
                    map[y1][x1] = 0;
                    y1 += (y2 > y1) ? 1 : -1;
                }
            } else {
                // Vertical first
                while (y1 !== y2) {
                    map[y1][x1] = 0;
                    y1 += (y2 > y1) ? 1 : -1;
                }
                while (x1 !== x2) {
                    map[y1][x1] = 0;
                    x1 += (x2 > x1) ? 1 : -1;
                }
            }
            
            // Mark the room as connected
            connectedRooms.add(bestConnection.toIdx);
            unconnectedRooms.delete(bestConnection.toIdx);
        } else {
            // Shouldn't happen, but break to avoid infinite loop
            break;
        }
    }
    
    // Final verification using flood fill
    const startRoom = rooms[0];
    const startX = Math.floor(startRoom.x + startRoom.width / 2);
    const startY = Math.floor(startRoom.y + startRoom.height / 2);
    const { visited, count } = floodFill(map, startX, startY);
    
    // Count total walkable tiles
    let totalWalkable = 0;
    for (let y = 0; y < map.length; y++) {
        for (let x = 0; x < map[0].length; x++) {
            if (map[y][x] === 0) totalWalkable++;
        }
    }
    
    console.log(`Connectivity check: ${count} tiles reachable out of ${totalWalkable} total walkable tiles`);
}

function generateRandomMap(width = 40, height = 40, wallType = 1) {
    // Everything starts as solid rock; rooms and corridors are cut out of it
    const map = Array(height).fill(null).map(() => Array(width).fill(wallType));
    const doorPositions = [];
    const rooms = [];

    const area = width * height;
    const targetRooms = Math.max(6, Math.min(20, Math.round(area / 110)));

    // Rooms keep two squares of rock between them, so walls always separate
    // neighbours and corridors have somewhere to run
    for (let i = 0; i < targetRooms; i++) {
        for (let attempt = 0; attempt < 80; attempt++) {
            const roomWidth = 4 + Math.floor(Math.random() * 6);
            const roomHeight = 4 + Math.floor(Math.random() * 6);
            const x = 2 + Math.floor(Math.random() * (width - roomWidth - 4));
            const y = 2 + Math.floor(Math.random() * (height - roomHeight - 4));

            const clashes = rooms.some((other) =>
                x - 2 < other.x + other.width && x + roomWidth + 2 > other.x &&
                y - 2 < other.y + other.height && y + roomHeight + 2 > other.y);
            if (clashes) continue;

            rooms.push({ x, y, width: roomWidth, height: roomHeight, shape: 'rectangle' });
            break;
        }
    }

    for (const room of rooms) {
        for (let y = room.y; y < room.y + room.height; y++) {
            for (let x = room.x; x < room.x + room.width; x++) {
                map[y][x] = 0;
            }
        }
    }

    const centreOf = (room) => [
        room.x + Math.floor(room.width / 2),
        room.y + Math.floor(room.height / 2),
    ];

    // A one-square corridor, tunnelled through the rock in an L
    function tunnel(from, to) {
        let [x, y] = from;
        const [tx, ty] = to;
        const horizontalFirst = Math.random() < 0.5;
        const stepX = () => { while (x !== tx) { map[y][x] = 0; x += x < tx ? 1 : -1; } };
        const stepY = () => { while (y !== ty) { map[y][x] = 0; y += y < ty ? 1 : -1; } };
        if (horizontalFirst) { stepX(); stepY(); } else { stepY(); stepX(); }
        map[ty][tx] = 0;
    }

    // Join the rooms into one network, each to its nearest neighbour so far
    if (rooms.length > 1) {
        const joined = [0];
        const waiting = rooms.map((_, i) => i).slice(1);
        const distance = (a, b) => {
            const [ax, ay] = centreOf(rooms[a]);
            const [bx, by] = centreOf(rooms[b]);
            return Math.abs(ax - bx) + Math.abs(ay - by);
        };

        while (waiting.length) {
            let best = { from: joined[0], at: 0, gap: Infinity };
            waiting.forEach((candidate, index) => {
                joined.forEach((inside) => {
                    const gap = distance(inside, candidate);
                    if (gap < best.gap) best = { from: inside, at: index, gap };
                });
            });
            const next = waiting.splice(best.at, 1)[0];
            tunnel(centreOf(rooms[best.from]), centreOf(rooms[next]));
            joined.push(next);
        }

        // A couple of loops, so the level is not a pure tree of dead ends
        const loops = 1 + Math.floor(rooms.length / 8);
        for (let i = 0; i < loops; i++) {
            const a = Math.floor(Math.random() * rooms.length);
            const b = Math.floor(Math.random() * rooms.length);
            if (a !== b) tunnel(centreOf(rooms[a]), centreOf(rooms[b]));
        }
    }

    // The odd pillar or bench in the larger halls
    for (const room of rooms) {
        if (room.width > 5 && room.height > 5 && Math.random() < 0.4) {
            const pattern = Math.floor(Math.random() * 3);
            if (pattern === 0) {
                [[1, 1], [room.width - 2, 1], [1, room.height - 2], [room.width - 2, room.height - 2]]
                    .forEach(([dx, dy]) => { map[room.y + dy][room.x + dx] = wallType; });
            } else if (pattern === 1) {
                map[room.y + Math.floor(room.height / 2)][room.x + Math.floor(room.width / 2)] = wallType;
            } else {
                for (let i = 2; i < room.width - 2; i += 3) {
                    map[room.y + 2][room.x + i] = wallType;
                    map[room.y + room.height - 3][room.x + i] = wallType;
                }
            }
        }
    }

    ensureConnectivity(map, rooms);
    
    // Find valid doorway positions after map is fully generated
    function findValidDoorways(map, width, height) {
        const validDoorways = [];
        
        // Check each position in the map
        for (let y = 1; y < height - 1; y++) {
            for (let x = 1; x < width - 1; x++) {
                // Skip if current position is not a floor
                if (map[y][x] !== 0) continue;
                
                // Check for horizontal doorway (walls above and below, passages left and right)
                if (map[y - 1][x] !== 0 && map[y + 1][x] !== 0 && // walls above and below
                    map[y][x - 1] === 0 && map[y][x + 1] === 0) { // passages left and right
                    
                    // Additional check: ensure it's a proper chokepoint
                    let wallsAround = 0;
                    for (let dy = -1; dy <= 1; dy++) {
                        for (let dx = -1; dx <= 1; dx++) {
                            if (Math.abs(dx) === Math.abs(dy)) continue; // skip diagonals and center
                            if (map[y + dy][x + dx] !== 0) wallsAround++;
                        }
                    }
                    
                    if (wallsAround >= 2) {
                        validDoorways.push({x, y, orientation: 'horizontal'});
                    }
                }
                
                // Check for vertical doorway (walls left and right, passages above and below)
                if (map[y][x - 1] !== 0 && map[y][x + 1] !== 0 && // walls left and right
                    map[y - 1][x] === 0 && map[y + 1][x] === 0) { // passages above and below
                    
                    // Additional check: ensure it's a proper chokepoint
                    let wallsAround = 0;
                    for (let dy = -1; dy <= 1; dy++) {
                        for (let dx = -1; dx <= 1; dx++) {
                            if (Math.abs(dx) === Math.abs(dy)) continue; // skip diagonals and center
                            if (map[y + dy][x + dx] !== 0) wallsAround++;
                        }
                    }
                    
                    if (wallsAround >= 2) {
                        validDoorways.push({x, y, orientation: 'vertical'});
                    }
                }
            }
        }
        
        return validDoorways;
    }
    
    // Place doors at valid doorway positions
    const validDoorways = findValidDoorways(map, width, height);
    
    // Sort doorways by distance from start to prioritize important passages
    const startRoom = rooms[0];
    const startX = Math.floor(startRoom.x + startRoom.width / 2);
    const startY = Math.floor(startRoom.y + startRoom.height / 2);
    
    validDoorways.sort((a, b) => {
        const distA = Math.abs(a.x - startX) + Math.abs(a.y - startY);
        const distB = Math.abs(b.x - startX) + Math.abs(b.y - startY);
        return distA - distB;
    });
    
    // Place doors at some of the valid doorways
    const maxDoors = Math.min(validDoorways.length, 3 + Math.floor(Math.random() * 4)); // 3-6 doors
    const doorSpacing = 3; // Minimum distance between doors
    
    for (let i = 0; i < validDoorways.length && doorPositions.length < maxDoors; i++) {
        const doorway = validDoorways[i];
        
        // Check if this doorway is far enough from existing doors
        let tooClose = false;
        for (const existingDoor of doorPositions) {
            const dist = Math.abs(doorway.x - existingDoor.x) + Math.abs(doorway.y - existingDoor.y);
            if (dist < doorSpacing) {
                tooClose = true;
                break;
            }
        }
        
        if (!tooClose) {
            // Door types:
            // 3 = horizontal_closed, 4 = horizontal_open
            // 5 = vertical_closed, 6 = vertical_open
            const isOpen = Math.random() < 0.3; // 30% chance of open door
            let doorType;
            
            if (doorway.orientation === 'horizontal') {
                doorType = isOpen ? 4 : 3;
            } else {
                doorType = isOpen ? 6 : 5;
            }
            
            map[doorway.y][doorway.x] = doorType;
            doorPositions.push({
                x: doorway.x, 
                y: doorway.y, 
                type: doorType,
                orientation: doorway.orientation,
                isOpen: isOpen
            });
        }
    }
    
    for (let y = 0; y < height; y++) {
        map[y][0] = wallType;
        map[y][width - 1] = wallType;
    }
    for (let x = 0; x < width; x++) {
        map[0][x] = wallType;
        map[height - 1][x] = wallType;
    }
    
    let startPosition = [1, 1];
    if (rooms.length > 0) {
        const startRoom = rooms[0];
        startPosition = [
            startRoom.x + Math.floor(startRoom.width / 2),
            startRoom.y + Math.floor(startRoom.height / 2)
        ];
    }
    
    // Add stairs up, as far from the start as we can manage. Every level must
    // have stairs or the run cannot continue, so this falls back until placed.
    {
        // Use flood fill to find all reachable tiles from start position
        const { visited } = floodFill(map, startPosition[0], startPosition[1]);
        const isFree = (x, y) => map[y][x] === 0 && visited[y][x] &&
            !(x === startPosition[0] && y === startPosition[1]);
        const distanceFromStart = (x, y) =>
            Math.abs(x - startPosition[0]) + Math.abs(y - startPosition[1]);

        let stairs = null;

        // Prefer a random spot in a room other than the starting one
        for (let attempts = 0; !stairs && attempts < 50 && rooms.length > 1; attempts++) {
            const stairsRoom = rooms[1 + Math.floor(Math.random() * (rooms.length - 1))];
            const stairsX = stairsRoom.x + 1 + Math.floor(Math.random() * (stairsRoom.width - 2));
            const stairsY = stairsRoom.y + 1 + Math.floor(Math.random() * (stairsRoom.height - 2));
            if (isFree(stairsX, stairsY)) {
                stairs = [stairsX, stairsY];
            }
        }

        // Otherwise take the reachable floor tile furthest from the party
        if (!stairs) {
            let best = -1;
            for (let y = 1; y < height - 1; y++) {
                for (let x = 1; x < width - 1; x++) {
                    if (isFree(x, y) && distanceFromStart(x, y) > best) {
                        best = distanceFromStart(x, y);
                        stairs = [x, y];
                    }
                }
            }
        }

        if (stairs) {
            map[stairs[1]][stairs[0]] = 7; // 7 = stairs up
            console.log(`Stairs placed at [${stairs[0]}, ${stairs[1]}]`);
        } else {
            console.warn('No reachable tile for stairs');
        }
    }

    // Add up to 3 chests in random rooms
    let keyChestLocation = null;
    if (rooms.length > 1) {
        const { visited } = floodFill(map, startPosition[0], startPosition[1]);
        
        // Determine number of chests (1-3)
        const numChests = Math.min(3, 1 + Math.floor(Math.random() * 3));
        const chestPositions = [];
        
        for (let i = 0; i < numChests; i++) {
            let chestPlaced = false;
            let attempts = 0;
            
            while (!chestPlaced && attempts < 50) {
                const chestRoomIndex = Math.floor(Math.random() * rooms.length);
                const chestRoom = rooms[chestRoomIndex];
                
                // Place chest in a random position within the room
                const chestX = chestRoom.x + 1 + Math.floor(Math.random() * (chestRoom.width - 2));
                const chestY = chestRoom.y + 1 + Math.floor(Math.random() * (chestRoom.height - 2));
                
                // Make sure chest is on a floor tile, reachable, and not on stairs or other chests
                if (map[chestY][chestX] === 0 && visited[chestY][chestX]) {
                    // Check not too close to other chests
                    let tooClose = false;
                    for (const pos of chestPositions) {
                        if (Math.abs(pos.x - chestX) + Math.abs(pos.y - chestY) < 3) {
                            tooClose = true;
                            break;
                        }
                    }
                    
                    if (!tooClose) {
                        map[chestY][chestX] = 8; // 8 = chest
                        chestPositions.push({x: chestX, y: chestY});
                        chestPlaced = true;
                        console.log(`Chest ${i + 1} placed at [${chestX}, ${chestY}]`);
                    }
                }
                attempts++;
            }
        }
        
        // Randomly select one chest to contain the key
        if (chestPositions.length > 0) {
            const keyChestIndex = Math.floor(Math.random() * chestPositions.length);
            keyChestLocation = chestPositions[keyChestIndex];
            console.log(`Key is in chest at [${keyChestLocation.x}, ${keyChestLocation.y}]`);
        }
    }
    
    return {
        tiles: map,
        start_position: startPosition,
        doors: doorPositions,
        keyChestLocation: keyChestLocation
    };
}

export default generateRandomMap;